<template>
  <b-modal
    id="modal-einvoice-status"
    ref="modalRef"
    title="Update E-Invoice Status"
    size="sm"
    centered
    no-close-on-backdrop
    @hidden="reset"
  >
    <div v-if="invoice" class="einvoice-status-modal text-center">
      <div class="einvoice-status-header mb-1">
        <strong class="d-block text-truncate">{{ invoice.invoice_id || invoice.invoice_number }}</strong>
        <small class="text-muted">{{ invoice.client_name || invoice.client }}</small>
      </div>

      <p class="text-muted small mb-2">Has the e-invoice been filed for this invoice?</p>

      <!-- Current status badge -->
      <b-badge
        :variant="invoice.einvoice_filed === true ? 'success' : invoice.einvoice_filed === false ? 'danger' : 'warning'"
        class="einvoice-status-badge mb-2"
      >
        <feather-icon
          :icon="invoice.einvoice_filed === true ? 'CheckCircleIcon' : invoice.einvoice_filed === false ? 'XCircleIcon' : 'ClockIcon'"
          size="12"
          class="mr-50"
        />
        {{ invoice.einvoice_filed === true ? 'E-Invoice Filed' : invoice.einvoice_filed === false ? 'E-Invoice Not Filed' : 'E-Invoice Pending' }}
      </b-badge>

      <!-- YES / NO compact pills — inline layout as backup for BV modal portal -->
      <div
        class="einvoice-choice-row"
        role="group"
        aria-label="E-Invoice filing status"
        style="display: flex; justify-content: center; align-items: center; gap: 8px; margin-top: 0;"
      >
        <button
          type="button"
          class="einvoice-choice-btn einvoice-choice-btn--yes"
          :class="{ 'is-selected': form.status === true }"
          :aria-pressed="form.status === true ? 'true' : 'false'"
          :style="choiceBtnStyle(true)"
          @click="form.status = true"
        >
          <span class="einvoice-choice-icon-wrap" style="display: inline-flex; align-items: center; line-height: 1; flex-shrink: 0;">
            <feather-icon icon="CheckIcon" size="14" />
          </span>
          <span class="einvoice-choice-text" style="display: inline-flex; align-items: baseline; white-space: nowrap; line-height: 1.2;">
            <span class="einvoice-choice-label" style="font-size: 13px; font-weight: 600;">YES</span>
            <span class="einvoice-choice-sep" style="font-size: 12px; font-weight: 400; opacity: 0.85; margin: 0 0.2em;">—</span>
            <span class="einvoice-choice-sublabel" style="font-size: 12px; font-weight: 400;">Filed</span>
          </span>
        </button>
        <button
          type="button"
          class="einvoice-choice-btn einvoice-choice-btn--no"
          :class="{ 'is-selected': form.status === false }"
          :aria-pressed="form.status === false ? 'true' : 'false'"
          :style="choiceBtnStyle(false)"
          @click="form.status = false"
        >
          <span class="einvoice-choice-icon-wrap" style="display: inline-flex; align-items: center; line-height: 1; flex-shrink: 0;">
            <feather-icon icon="XIcon" size="14" />
          </span>
          <span class="einvoice-choice-text" style="display: inline-flex; align-items: baseline; white-space: nowrap; line-height: 1.2;">
            <span class="einvoice-choice-label" style="font-size: 13px; font-weight: 600;">NO</span>
            <span class="einvoice-choice-sep" style="font-size: 12px; font-weight: 400; opacity: 0.85; margin: 0 0.2em;">—</span>
            <span class="einvoice-choice-sublabel" style="font-size: 12px; font-weight: 400;">Not Filed</span>
          </span>
        </button>
      </div>

      <!-- Optional date and notes when YES -->
      <div v-if="form.status === true" class="mt-2 text-left">
        <b-form-group label="Filing Date" label-size="sm" class="mb-0">
          <b-form-input
            v-model="form.filingDate"
            type="date"
            size="sm"
            :max="todayDate"
            placeholder="Select filing date"
          />
        </b-form-group>
      </div>

      <!-- Reason when NO -->
      <div v-if="form.status === false" class="mt-2 text-left">
        <b-form-group label="Reason / Notes" label-size="sm" class="mb-1">
          <b-form-select v-model="form.reason" :options="notFiledReasons" size="sm" />
        </b-form-group>
        <b-form-group v-if="form.reason === 'Other'" label="Comments" label-size="sm" class="mb-0">
          <b-form-textarea v-model="form.comments" rows="2" size="sm" placeholder="Enter reason..." />
        </b-form-group>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="outline-secondary" size="sm" @click="$refs.modalRef.hide()">Cancel</b-button>
      <b-button
        variant="primary"
        size="sm"
        :disabled="form.status === null || saving"
        @click="save"
      >
        <b-spinner v-if="saving" small class="mr-1" />
        Save
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BBadge, BButton, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BSpinner } from 'bootstrap-vue'
import moment from 'moment'

export default {
  name: 'EInvoiceStatusModal',
  components: { BModal, BBadge, BButton, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BSpinner },
  data() {
    return {
      invoice: null,
      saving: false,
      form: {
        status: null,
        filingDate: moment().format('YYYY-MM-DD'),
        reason: null,
        comments: ''
      },
      notFiledReasons: [
        { value: null, text: 'Select reason...' },
        { value: 'Not Booked', text: 'Invoice not booked yet' },
        { value: 'Pending Approval', text: 'Pending client approval' },
        { value: 'Data Mismatch', text: 'Data mismatch / correction needed' },
        { value: 'Cancelled', text: 'Invoice cancelled' },
        { value: 'Other', text: 'Other (specify)' }
      ]
    }
  },
  computed: {
    todayDate() {
      return moment().format('YYYY-MM-DD')
    }
  },
  methods: {
    normalizeEinvoiceFiled(value) {
      if (value === true || value === 1) return true
      if (value === false || value === 0) return false
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (normalized === 'true' || normalized === 'yes' || normalized === 'filed') return true
        if (normalized === 'false' || normalized === 'no' || normalized === 'not filed') return false
      }
      return null
    },
    choiceBtnStyle(isYes) {
      const selected = this.form.status === isYes
      const color = isYes ? '#28c76f' : '#ea5455'
      const selectedBg = isYes ? 'rgba(40, 199, 111, 0.15)' : 'rgba(234, 84, 85, 0.15)'
      return {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flex: '1 1 0',
        minWidth: '0',
        minHeight: '34px',
        padding: '6px 10px',
        border: `2px solid ${color}`,
        borderRadius: '999px',
        background: selected ? selectedBg : 'transparent',
        cursor: 'pointer',
        color,
        lineHeight: 1.2
      }
    },
    show(invoice) {
      this.invoice = {
        ...invoice,
        einvoice_filed: this.normalizeEinvoiceFiled(invoice.einvoice_filed)
      }
      this.form.status = this.invoice.einvoice_filed === true ? true : this.invoice.einvoice_filed === false ? false : null
      this.form.filingDate = invoice.einvoice_date || moment().format('YYYY-MM-DD')
      this.form.reason = null
      this.form.comments = ''
      this.saving = false
      this.$nextTick(() => this.$refs.modalRef && this.$refs.modalRef.show())
    },
    reset() {
      this.invoice = null
      this.form = { status: null, filingDate: moment().format('YYYY-MM-DD'), reason: null, comments: '' }
      this.saving = false
    },
    async save() {
      if (this.form.status === null) return
      this.saving = true
      try {
        const payload = {
          status: this.form.status,
          filingDate: this.form.status ? this.form.filingDate : null,
          reason: this.form.status ? null : (this.form.reason || null),
          comments: this.form.comments || null
        }
        this.$emit('save', { invoice: this.invoice, payload })
        this.$refs.modalRef && this.$refs.modalRef.hide()
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<!--
  Unscoped on purpose: b-modal portals content to <body>, and scoped attribute
  selectors often fail to style the teleported nodes (default <button> then
  collapses icon+YES+Filed into one line: "✓YESFiled"). Unique einvoice-* prefix
  avoids collisions.
-->
<style lang="scss">
.einvoice-status-modal {
  padding: 0 0.25rem 0.25rem;
}

.einvoice-status-header {
  line-height: 1.25;
}

.einvoice-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.3rem 0.65rem;
  line-height: 1.2;
}

.einvoice-choice-row {
  display: flex !important;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 0;
}

.einvoice-choice-btn {
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.375rem;
  flex: 1 1 0;
  min-width: 0;
  min-height: 2.125rem;
  padding: 0.375rem 0.625rem !important;
  border-width: 2px !important;
  border-style: solid !important;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  line-height: 1.2;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
  }

  .einvoice-choice-icon-wrap {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 1;

    svg {
      display: block;
    }
  }

  .einvoice-choice-text {
    display: inline-flex;
    align-items: baseline;
    white-space: nowrap;
    line-height: 1.2;
  }

  .einvoice-choice-label {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .einvoice-choice-sep {
    font-size: 0.75rem;
    font-weight: 400;
    opacity: 0.85;
    margin: 0 0.15em;
  }

  .einvoice-choice-sublabel {
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.2;
  }
}

.einvoice-choice-btn--yes {
  border-color: #28c76f !important;
  color: #28c76f !important;

  &:hover:not(.is-selected) {
    background: rgba(40, 199, 111, 0.08);
  }

  &.is-selected {
    background: rgba(40, 199, 111, 0.15) !important;
    border-color: #28c76f !important;
    color: #28c76f !important;
  }
}

.einvoice-choice-btn--no {
  border-color: #ea5455 !important;
  color: #ea5455 !important;

  &:hover:not(.is-selected) {
    background: rgba(234, 84, 85, 0.08);
  }

  &.is-selected {
    background: rgba(234, 84, 85, 0.15) !important;
    border-color: #ea5455 !important;
    color: #ea5455 !important;
  }
}
</style>
